import { useState } from 'react'
import { App, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import promotionService from '../services/promotion.service'
import type { Promotion, CreatePromotionData, UpdatePromotionData } from '../types/promotion.types'

export const usePromotionActions = () => {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null)
    const queryClient = useQueryClient()
    const { message } = App.useApp()

    const createMutation = useMutation({
        mutationFn: (data: CreatePromotionData) => promotionService.createPromotion(data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['promotions'] })
            message.success('Promotion created successfully!')
            setIsFormModalOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to create promotion')
        },
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdatePromotionData }) =>
            promotionService.updatePromotion(id, data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['promotions'] })
            message.success('Promotion updated successfully!')
            setIsFormModalOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to update promotion')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: (id: string) => promotionService.deletePromotion(id),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['promotions'] })
            message.success('Promotion deleted successfully!')
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to delete promotion')
        },
    })

    const handleAddPromotion = () => {
        setEditingPromotion(null)
        setIsFormModalOpen(true)
    }

    const handleEditPromotion = (promotion: Promotion) => {
        setEditingPromotion(promotion)
        setIsFormModalOpen(true)
    }

    const handleDeletePromotion = (promotion: Promotion) => {
        Modal.confirm({
            title: 'Delete Promotion',
            content: `Are you sure you want to delete "${promotion.title}"?`,
            okText: 'Delete',
            okType: 'danger',
            onOk: () => {
                deleteMutation.mutate(promotion.id)
            },
        })
    }

    const handleFormSubmit = async (values: any) => {
        const payload = {
            title: values.title,
            subtitle: values.subtitle,
            image_url: values.image_url,
            colors: values.colors || ['#1e293b', '#0f172a'],
            action_url: values.action_url,
            status: values.status || 'active',
        }

        if (editingPromotion) {
            await updateMutation.mutateAsync({ id: editingPromotion.id, data: payload })
        } else {
            await createMutation.mutateAsync(payload)
        }
    }

    const closeFormModal = () => setIsFormModalOpen(false)

    return {
        isFormModalOpen,
        editingPromotion,
        handleAddPromotion,
        handleEditPromotion,
        handleDeletePromotion,
        handleFormSubmit,
        closeFormModal,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
    }
}
